import React from 'react';
import { Map, fromJS } from 'immutable';
import { call, put, takeEvery } from 'redux-saga/effects';
import { connect } from 'react-redux';
import {
  fetchSystemLicense,
  fetchSystemLicenseCheck,
  deleteSystemLicense,
  resetSystemLicense,
} from '@kineticdata/react';
import { Icon } from '@kineticdata/ui';
import {
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import { action, dispatch, regHandlers, regRoute, regSaga } from '../../store';
import { openModal } from '../common/ConfirmationModal';
import { SystemLicenseImportButton } from './SystemLicenseImportButton';
import classNames from 'classnames';

regHandlers({
  SET_SYSTEM_LICENSE: (state, { payload }) =>
    state.setIn(['page', 'systemLicensing'], fromJS(payload)),
  SET_SYSTEM_LICENSE_ERROR: (state, { payload }) => state,
});

function* fetchSystemLicenseTask() {
  const { system: license, error: licenseError } = yield call(
    fetchSystemLicense,
  );
  const { system: status, error: statusError } = yield call(
    fetchSystemLicenseCheck,
  );

  if (licenseError || statusError) {
    yield put(
      action('SET_SYSTEM_LICENSE_ERROR', {
        licenseError,
        statusError,
        loading: false,
      }),
    );
  } else {
    yield put(
      action('SET_SYSTEM_LICENSE', { license, status, loading: false }),
    );
  }
}

regRoute('SYSTEM_LICENSE', '/system/licensing', {
  enterTask: fetchSystemLicenseTask,
});

regSaga(takeEvery('REFETCH_SYSTEM_LICENSE', fetchSystemLicenseTask));
regSaga(
  takeEvery('RESET_SYSTEM_LICENSE', function*() {
    yield call(deleteSystemLicense);
    yield put(action('REFETCH_SYSTEM_LICENSE'));
  }),
);
regSaga(
  takeEvery('RESTART_SUBMISSION_ENGINE', function*() {
    yield call(resetSystemLicense);
    yield put(action('REFETCH_SYSTEM_LICENSE'));
  }),
);

const mapStateToProps = state => ({
  license: state.getIn(['page', 'systemLicensing', 'license'], ''),
  status: state.getIn(['page', 'systemLicensing', 'status'], Map()),
  licenseError: state.getIn(['page', 'systemLicensing', 'licenseError'], ''),
  engineError: state.getIn(['page', 'systemLicensing', 'engineError'], ''),
  loading: state.getIn(['page', 'systemLicensing', 'loading'], true),
});

const handleResetLicense = () =>
  openModal({
    ok: action('RESET_SYSTEM_LICENSE'),
    title: 'Reset System License?',
    body: 'Are you sure you want to reset your system license?',
    actionName: 'Reset License',
    actionType: 'danger',
  });
const handleRestartSubmissionEngine = () =>
  dispatch(action('RESTART_SUBMISSION_ENGINE'));
const onSuccess = () => dispatch(action('REFETCH_SYSTEM_LICENSE'));

export const SystemLicense = connect(mapStateToProps)(props => {
  const { license, status, licenseError, engineError, loading } = props;
  const engineStatus = engineError || status.get('application', 'Unknown');
  const licenseStatus = licenseError || status.get('license', 'Unknown');

  const licenseHref = window.URL.createObjectURL(
    new Blob([license], { type: 'text/plain' }),
  );

  return !loading ? (
    <div className="app-content__main">
      <div className="heading">
        <h1>System Licensing</h1>
        <div className="btn-set">
          <UncontrolledDropdown>
            <DropdownToggle
              tag="button"
              className="btn btn-outline-primary"
              title="Actions"
            >
              <Icon name="more-vertical" />
            </DropdownToggle>
            <DropdownMenu end>
              <SystemLicenseImportButton onSuccess={onSuccess}>
                {({ onClick }) => (
                  <DropdownItem onClick={onClick}>
                    <Icon name="upload" sm />
                    <span>Import License</span>
                  </DropdownItem>
                )}
              </SystemLicenseImportButton>
              <DropdownItem tag="a" download="license.txt" href={licenseHref}>
                <Icon name="download" sm />
                <span>Export License</span>
              </DropdownItem>
              <DropdownItem onClick={handleResetLicense}>
                <Icon name="rotate-ccw" sm />
                <span>Reset License</span>
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        </div>
      </div>

      <div className="content-container">
        <div className="heading heading-sm">
          <h2 className="h3">Submission Engine Status</h2>
        </div>
        <div className="input-group input-group--map-field">
          <div
            className={classNames('form-control-plaintext rounded', {
              'animate-success-bg': engineStatus === 'Running',
              'bg-light': engineStatus === 'Unknown',
              'bg-danger':
                engineStatus !== 'Running' && engineStatus !== 'Unknown',
            })}
            style={{ maxWidth: '40vw' }}
          />
          {engineStatus !== 'Running' && (
            <button
              className="btn btn-outline-primary"
              onClick={handleRestartSubmissionEngine}
            >
              <span>Start</span>
            </button>
          )}
        </div>
        <div className="form-control-plaintext">
          <em className="fw-semibold">{engineStatus}</em>
        </div>

        <div className="heading heading-sm mt-4">
          <h2 className="h3">License Status</h2>
        </div>
        <div
          className={classNames('form-control-plaintext rounded', {
            'animate-success-bg': licenseStatus === 'Active',
            'bg-light': licenseStatus === 'Unknown',
            'bg-danger':
              licenseStatus !== 'Active' && licenseStatus !== 'Unknown',
          })}
          style={{ maxWidth: '40vw' }}
        />
        <div className="form-control-plaintext">
          <em className="fw-semibold">{licenseStatus}</em>
        </div>

        <div className="heading heading-sm mt-4">
          <h2 className="h3">Licensing Key</h2>
        </div>
        <div className="form-control form-control--code text-pre">
          {license || 'Unlicensed Mode'}
        </div>
      </div>
    </div>
  ) : null;
});
